import { useState, useEffect, useMemo } from "react";
import { AlertTriangle, Download, Printer, FileSpreadsheet, Search, Loader2 } from "lucide-react";
import { apiService } from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { useLang } from "@/hooks/useLang";

const t = {
  fr: {
    title: "Élèves Exclus", subtitle: "Liste des élèves exclus par le conseil de classe",
    year: "Année", class: "Classe", allClasses: "Toutes", total: "Total",
    avg: "Moy.", search: "Rechercher...", loading: "Chargement...",
    noData: "Aucun élève exclus", print: "Imprimer", exportExcel: "Excel",
    matricule: "Matricule", name: "Nom", reason: "Motif", exclusionDays: "Jours excl.",
    unjustifiedAbs: "Abs. non just.", conduct: "Conduite",
  },
  en: {
    title: "Excluded Students", subtitle: "List of students excluded by class council",
    year: "Year", class: "Class", allClasses: "All", total: "Total",
    avg: "Avg", search: "Search...", loading: "Loading...",
    noData: "No excluded students", print: "Print", exportExcel: "Excel",
    matricule: "ID", name: "Name", reason: "Reason", exclusionDays: "Excl. days",
    unjustifiedAbs: "Unjust. abs.", conduct: "Conduct",
  }
};

export default function CouncilExcludedPage() {
  const { lang } = useLang();
  const txt = t[lang];
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [academicYears, setAcademicYears] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [students, setStudents] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const schoolId = user?.school_id || 1;

  useEffect(() => {
    apiService.get(`/schools/${schoolId}/academic-years`).then((res: any) => {
      const list = Array.isArray(res) ? res : res?.data || res?.items || [];
      setAcademicYears(list);
      if (list[0]) setSelectedYear(String(list[0].id));
    });
    apiService.get("/classes", { params: { school_id: schoolId, per_page: 1000 } }).then((res: any) => {
      setClasses(Array.isArray(res) ? res : res?.data || res?.items || []);
    });
  }, [schoolId]);

  useEffect(() => {
    if (!selectedYear) return;
    setLoading(true);
    const params: any = { academic_year_id: selectedYear };
    if (selectedClass) params.class_id = selectedClass;
    apiService.get("/class-council/reports/exclus", { params })
      .then((res: any) => setStudents((res?.data || res)?.items || []))
      .catch(() => toast.error(txt.loading))
      .finally(() => setLoading(false));
  }, [selectedYear, selectedClass]);

  const filtered = useMemo(() => {
    if (!searchQuery) return students;
    const q = searchQuery.toLowerCase();
    return students.filter((s: any) => (s.full_name || "").toLowerCase().includes(q) || (s.matricule || "").toLowerCase().includes(q));
  }, [students, searchQuery]);

  const exportExcel = () => {
    const headers = ["Matricule", "Nom", "Classe", "Moyenne", "Abs. non just.", "Jours exclusion", "Avert. conduite", "Blâme conduite"];
    const rows = filtered.map((s: any) => [s.matricule, `${s.first_name} ${s.last_name}`, s.class_name, s.general_average || "-", s.absence_unjustified || 0, s.exclusion_days || 0, s.avertissement_conduite ? "Oui" : "Non", s.blame_conduite ? "Oui" : "Non"]);
    const csv = [headers.join(";"), ...rows.map((r: any) => r.join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `exclus_${selectedYear}.csv`;
    link.click();
    toast.success("Excel téléchargé");
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 print:p-0">
      <div className="mb-6 print:hidden">
        <h1 className="text-2xl font-bold text-red-700 flex items-center gap-2">
          <AlertTriangle className="w-7 h-7" /> {txt.title}
        </h1>
        <p className="text-sm text-gray-500">{txt.subtitle}</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4 print:hidden flex flex-wrap items-center gap-3">
        <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
          <option value="">{txt.year}</option>
          {academicYears.map(y => <option key={y.id} value={String(y.id)}>{y.name || y.label}</option>)}
        </select>
        <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
          <option value="">{txt.allClasses}</option>
          {classes.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
        </select>
        <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-bold">{filtered.length} {txt.total}</span>
        <div className="flex-1" />
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" placeholder={txt.search} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 border rounded-lg text-sm w-40" />
        </div>
        <button onClick={() => window.print()} className="p-2 hover:bg-gray-100 rounded-lg"><Printer className="w-4 h-4" /></button>
        <button onClick={exportExcel} className="p-2 hover:bg-gray-100 rounded-lg text-emerald-600"><FileSpreadsheet className="w-4 h-4" /></button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase">{txt.matricule}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase">{txt.name}</th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase">{txt.class}</th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase">{txt.avg}</th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase">{txt.unjustifiedAbs}</th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase">{txt.exclusionDays}</th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase">{txt.conduct}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan={7} className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="py-12 text-center text-gray-400">{txt.noData}</td></tr>
            ) : filtered.map((s: any, i: number) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-[#1e3a8a]">{s.matricule}</td>
                <td className="px-4 py-3 font-medium">{s.first_name} {s.last_name}</td>
                <td className="px-4 py-3 text-center">{s.class_name}</td>
                <td className="px-4 py-3 text-center font-bold">{s.general_average ? parseFloat(s.general_average).toFixed(2) : "-"}</td>
                <td className="px-4 py-3 text-center text-red-600 font-bold">{s.absence_unjustified || 0}</td>
                <td className="px-4 py-3 text-center text-red-600 font-bold">{s.exclusion_days || 0}</td>
                <td className="px-4 py-3 text-center">
                  {s.avertissement_conduite && <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-[10px] font-bold">Avert.C</span>}
                  {s.blame_conduite && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold ml-1">Blâme C</span>}
                  {!s.avertissement_conduite && !s.blame_conduite && <span className="text-gray-300">-</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}